import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ClearIcon from "@mui/icons-material/Clear";
import { styled } from "@mui/material/styles";
import {
  createteamMember,
  resetTeamMemberState,
  updateTeamMembers,
} from "../../redux/slices/teamMemberSlice";
import { useNavigate, useLocation } from "react-router-dom"; // Add useLocation
import { useDispatch } from "react-redux";
import Notification from "../../components/common/Notification";
import { useAppSelector } from "../../redux/hooks";
import Loader from "../../components/common/Loader";
import JoditEditor from "jodit-react";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ImageDropZone = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 350,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: theme.palette.grey[50],
  marginBottom: theme.spacing(3),
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100],
  },
}));

const PreviewContainer = styled(Box)({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
});

const PreviewImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  maxWidth: "100%",
  maxHeight: "350px",
});

const StyledFormContainer = styled(Box)({
  width: "100%",
  height: "100%",
  backgroundColor: "background.default",
});

const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  height: "100%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const StyledTypography = styled(Typography)({
  fontFamily: "Poppins, sans-serif !important",
});

const Form = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData; // Get editData from navigation state
  const dispatch = useDispatch();
  const editor = useRef(null);

  const { loading, error, success } = useAppSelector(
    (state) => state.teamMember
  );
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    roleFlag: "office_bearers",
    status: "active",
    image: null,
    preview: null,
    altTags: "",
    description: "",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    slug: "",
  });

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Enter description here...",
      height: 300,
      buttons: [
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "|",
        "link",
        "image",
      ],
      width: "100%",
    }),
    []
  );

  useEffect(() => {
    if (success) {
      setNotification({
        type: "success",
        message: editData
          ? "Team member updated successfully!"
          : "Team member created successfully!",
      });
      setTimeout(() => {
        setNotification(null);
        dispatch(resetTeamMemberState());
        navigate("/team-members", { replace: true }); // Navigate back to list
      }, 2000);
    }
  }, [success, editData, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      setNotification({
        type: "error",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Something went wrong!",
      });
    }
  }, [error]);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        role: editData.role || "",
        status: editData.status || "active",
        image: null,
        preview: typeof editData.image === "string" ? editData.image : null,
        roleFlag: editData.roleFlag || "office_bearers",
        altTags: editData.altTags || "",
        description: editData.description || "",
        metaTitle: editData.metaTitle || "",
        metaDescription: editData.metaDescription || "",
        metaKeywords: editData.metaKeywords || "",
        slug: editData.slug || "",
      });
    }
  }, [editData]);

  // Update the onClose/navigation logic
  const handleClose = () => {
    navigate("/team-member");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "altTags") {
      // Clean up alt tags by removing extra spaces and formatting correctly
      const cleanedTags = value
        .split(",")
        .map((tag) => tag.trim()) // Remove spaces from each tag
        .filter((tag) => tag) // Remove empty tags
        .join(","); // Join back with just commas

      setFormData((prev) => ({
        ...prev,
        [name]: cleanedTags,
      }));
    } else if (name === "name") {
      // Auto-generate slug when name changes
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        slug: slug, // Auto-update slug
      }));
    } else {
      // Handle other fields normally
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setNotification(null);

    if (file.size < 1024 || file.size > 2 * 1024 * 1024) {
      setNotification({
        type: "error",
        message: "Image size must be between 1KB and 2MB",
      });
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== 604 || img.height !== 702) {
        setNotification({
          type: "error",
          message: "Image dimensions must be exactly 604px × 702px",
        });
        URL.revokeObjectURL(img.src);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
        preview: img.src,
      }));
    };

    img.onerror = () => {
      setNotification({
        type: "error",
        message: "Invalid image file format",
      });
      URL.revokeObjectURL(img.src);
    };
  };

  const handleRemoveImage = () => {
    if (formData.preview) {
      URL.revokeObjectURL(formData.preview);
    }
    setFormData((prev) => ({
      ...prev,
      image: null,
      preview: null,
    }));
    setNotification(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous notifications
    setNotification(null);

    const formDataObj = new FormData();
    formDataObj.append("name", formData.name.trim());
    formDataObj.append("slug", formData.slug.trim());
    formDataObj.append("role", formData.role.trim());
    formDataObj.append("roleFlag", formData.roleFlag);
    formDataObj.append("status", formData.status);
    formDataObj.append("altTags", formData.altTags);
    formDataObj.append("description", formData.description);
    formDataObj.append("metaTitle", formData.metaTitle);
    formDataObj.append("metaDescription", formData.metaDescription);
    formDataObj.append("metaKeywords", formData.metaKeywords);

    if (formData.image) {
      formDataObj.append("image", formData.image);
    }

    try {
      if (editData) {
        const result = await dispatch(
          updateTeamMembers({
            id: editData._id,
            data: formDataObj,
          })
        ).unwrap();

        if (result) {
          setNotification({
            type: "success",
            message: "Team member updated successfully!",
          });

          // Navigate after showing success message
          setTimeout(() => {
            navigate("/team-member", { replace: true });
          }, 1500);
        }
      } else {
        const result = await dispatch(createteamMember(formDataObj)).unwrap();

        if (result) {
          setNotification({
            type: "success",
            message: "Team member created successfully!",
          });

          // Navigate after showing success message
          setTimeout(() => {
            navigate("/team-member", { replace: true });
          }, 1500);
        }
      }
    } catch (err) {
      console.error("Form submission error:", err);

      // Show complete backend error
      setNotification({
        type: "error",
        message: err || "Operation failed. Please try again.",
      });
    }
  };

  // Update the useEffect for success handling:
  useEffect(() => {
    if (success) {
      // Remove this useEffect since we're handling success in handleSubmit
      dispatch(resetTeamMemberState());
    }
  }, [success, dispatch]);

  // Update the useEffect for error handling:
  useEffect(() => {
    if (error) {
      setNotification({
        type: "error",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Something went wrong!",
      });
    }
  }, [error]);

  return (
    <StyledFormContainer className="poppins-font">
      {loading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </Box>
      )}

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
        className="poppins-font"
      >
        <IconButton
          onClick={handleClose} // Use handleClose instead of onClose
          sx={{
            mr: 2,
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <StyledTypography variant="h5" component="h1" fontWeight="bold">
          {editData ? "Edit" : "Add New"} Team Member
        </StyledTypography>
      </Box>

      {/* Main Form Content */}
      <Box sx={{ p: 3 }}>
        <form onSubmit={handleSubmit} className="poppins-font">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* First Row: Profile Image + Basic Info */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
              }}
            >
              {/* Profile Image - Takes 40% width */}
              <StyledFormSection sx={{ flex: 4 }}>
                <Typography variant="h6" className="poppins-font" gutterBottom>
                  Profile Image
                </Typography>
                <ImageDropZone>
                  {formData.preview ? (
                    <PreviewContainer>
                      <PreviewImage src={formData.preview} alt="Preview" />
                      <IconButton
                        onClick={handleRemoveImage}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "background.paper",
                          "&:hover": {
                            backgroundColor: "background.default",
                          },
                        }}
                      >
                        <ClearIcon color="error" />
                      </IconButton>
                    </PreviewContainer>
                  ) : (
                    <Box sx={{ textAlign: "center" }}>
                      <CloudUploadIcon
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      />
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Drag & drop image here or click to browse
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        (Image must be 604px × 702px and between 1KB-2MB)
                      </Typography>
                      <Button
                        component="label"
                        variant="contained"
                        size="large"
                        startIcon={<CloudUploadIcon />}
                      >
                        Upload Image
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>
                    </Box>
                  )}
                </ImageDropZone>
              </StyledFormSection>

              {/* Basic Information - Takes 60% width */}
              <StyledFormSection sx={{ flex: 6 }}>
                <Typography
                  variant="h6"
                  className="poppins-font"
                  gutterBottom
                  sx={{ mb: 3 }}
                >
                  Basic Information
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Name *"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="poppins-font"
                    InputProps={{
                      className: "poppins-font",
                    }}
                    InputLabelProps={{
                      className: "poppins-font",
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    helperText="URL-friendly version of the name"
                    className="poppins-font"
                    InputProps={{
                      className: "poppins-font",
                    }}
                    InputLabelProps={{
                      className: "poppins-font",
                    }}
                  />
                  <FormControl fullWidth required>
                    <InputLabel className="poppins-font">Role *</InputLabel>
                    <Select
                      name="role"
                      value={formData.role}
                      label="Role *"
                      onChange={handleChange}
                      className="poppins-font"
                      MenuProps={{
                        classes: { paper: "poppins-font" },
                      }}
                    >
                      <MenuItem
                        className="poppins-font"
                        value="Founder & President"
                      >
                        Founder & President
                      </MenuItem>
                      <MenuItem className="poppins-font" value="Vice President">
                        Vice President
                      </MenuItem>
                      <MenuItem className="poppins-font" value="Secretary">
                        Secretary
                      </MenuItem>
                      <MenuItem
                        className="poppins-font"
                        value="Hon. Joint Secretary"
                      >
                        Hon. Joint Secretary
                      </MenuItem>
                      <MenuItem className="poppins-font" value="Hon. Treasurer">
                        Hon. Treasurer
                      </MenuItem>
                      <MenuItem className="poppins-font" value="CEO">
                        CEO
                      </MenuItem>
                      <MenuItem className="poppins-font" value="Trustee">
                        Trustee
                      </MenuItem>
                      <MenuItem className="poppins-font" value="Volunteer">
                        Volunteer
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth required sx={{ flex: 1 }}>
                      <InputLabel className="poppins-font">
                        Role Flag *
                      </InputLabel>
                      <Select
                        name="roleFlag"
                        value={formData.roleFlag}
                        label="Role Flag *"
                        onChange={handleChange}
                        className="poppins-font"
                      >
                        <MenuItem value="office_bearers">
                          Office Bearers
                        </MenuItem>
                        <MenuItem value="our_trustees">Our Trustees</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth required sx={{ flex: 1 }}>
                      <InputLabel className="poppins-font">Status *</InputLabel>
                      <Select
                        name="status"
                        value={formData.status}
                        label="Status *"
                        onChange={handleChange}
                        className="poppins-font"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </StyledFormSection>
            </Box>

            {/* Second Row: Alt Tags & Description (Full width) */}
            <StyledFormSection>
              <Typography
                variant="h6"
                className="poppins-font"
                gutterBottom
                sx={{ mb: 2 }}
              >
                Alt Tags & Description
              </Typography>
              <TextField
                fullWidth
                label="Alt Tags"
                name="altTags"
                value={formData.altTags}
                onChange={handleChange}
                margin="normal"
                helperText="Enter tags separated by commas (e.g., A,B,C)"
                className="poppins-font"
              />
              <Box sx={{ my: 2 }}>
                {formData.altTags
                  .split(",")
                  .filter((tag) => tag.trim())
                  .map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag.trim()}
                      onDelete={() => {
                        const newTags = formData.altTags
                          .split(",")
                          .filter((_, i) => i !== index)
                          .join(",");
                        setFormData((prev) => ({
                          ...prev,
                          altTags: newTags,
                        }));
                      }}
                      sx={{ m: 0.5 }}
                      className="poppins-font"
                    />
                  ))}
              </Box>

              <Typography
                variant="subtitle1"
                className="poppins-font"
                gutterBottom
                sx={{ mt: 3, mb: 2 }}
              >
                Description
              </Typography>
              <Box sx={{ mb: 3 }}>
                <JoditEditor
                  ref={editor}
                  value={formData.description}
                  config={editorConfig}
                  onBlur={(newContent) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: newContent,
                    }))
                  }
                />
              </Box>
            </StyledFormSection>

            {/* Third Row: SEO Settings (Full width) */}
            <StyledFormSection>
              <Typography className="poppins-font" variant="h6" gutterBottom>
                SEO Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 3,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Meta Title"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleChange}
                    className="poppins-font"
                    InputProps={{
                      className: "poppins-font",
                    }}
                    InputLabelProps={{
                      className: "poppins-font",
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Meta Keywords"
                    name="metaKeywords"
                    value={formData.metaKeywords}
                    onChange={handleChange}
                    helperText="Enter keywords separated by commas"
                    className="poppins-font"
                    InputProps={{
                      className: "poppins-font",
                    }}
                    InputLabelProps={{
                      className: "poppins-font",
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Meta Description"
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  className="poppins-font"
                  InputProps={{
                    className: "poppins-font",
                  }}
                  InputLabelProps={{
                    className: "poppins-font",
                  }}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                {formData.metaKeywords.split(",").map((keyword, index) =>
                  keyword.trim() ? (
                    <Chip
                      key={index}
                      label={keyword.trim()}
                      onDelete={() => {
                        const newKeywords = formData.metaKeywords
                          .split(",")
                          .filter((_, i) => i !== index)
                          .join(",");
                        setFormData((prev) => ({
                          ...prev,
                          metaKeywords: newKeywords,
                        }));
                      }}
                      sx={{ m: 0.5 }}
                      className="poppins-font"
                    />
                  ) : null
                )}
              </Box>
            </StyledFormSection>
          </Box>

          {/* Form Actions */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              py: 2,
              mt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              zIndex: 1000,
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120 }}
              className="poppins-font"
            >
              {loading ? "Saving..." : editData ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </Box>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
    </StyledFormContainer>
  );
};

export default Form;
